// Zenova Mock Data - Business Systemization for Indian SMEs

export const heroData = {
  tagline: "Growth Without Chaos",
  headline: "Build a Business That Runs Without You",
  subheadline: "System-Driven Growth for Indian SMEs",
  description: "You didn't start your business to spend your days firefighting, approving everything, and solving the same problems again and again. At Zenova, we help SME owners transform people-dependent, chaos-driven businesses into system-run, future-ready organizations.",
  stats: [
    { value: 90, suffix: " Days", label: "Transformation Timeline" },
    { value: 85, suffix: "%", label: "Reduced Owner Dependency" },
    { value: 3, suffix: "x", label: "Revenue Growth Potential" },
    { value: 3, suffix: "x", label: "Output with same resources" }
  ],
  features: [
    "Systems that scale.",
    "Protocols that create clarity.",
    "Freedom without losing control."
  ],
  ctaText: "Book Your 7-Min Demo",
  secondaryCtaText: "Calculate Your Savings"
};

export const painPointsData = {
  title: "When Growth Starts Feeling Heavy",
  subtitle: "Your revenue may be increasing — but so is the pressure.",
  problems: [
    {
      icon: "AlertCircle",
      title: "Every Decision Comes to You",
      description: "You've become the bottleneck. Nothing moves without your approval, leaving you exhausted and your team waiting."
    },
    {
      icon: "Clock",
      title: "Every Delay Lands on Your Desk",
      description: "When something goes wrong, it's always your problem to solve. You're constantly firefighting instead of growing."
    },
    {
      icon: "Users",
      title: "Key People Can Never Take Leave",
      description: "Your business depends on specific individuals. If they're absent, operations crumble."
    },
    {
      icon: "MessageCircle",
      title: "WhatsApp Instead of Systems",
      description: "Critical information lives in chat groups and verbal instructions, not documented processes."
    }
  ],
  conclusion: "You are not running your business anymore. You are holding it together.",
  truth: "And the truth is — this doesn't scale."
};

export const beliefData = {
  title: "We Believe Growth Should Feel Inspiring — Not Exhausting",
  subtitle: "Behind every business is a founder's dream. But too often, that dream gets buried under daily chaos.",
  beliefs: [
    {
      icon: "Lightbulb",
      text: "Systems should create clarity, not confusion"
    },
    {
      icon: "BarChart3",
      text: "KPIs should guide teams, not pressure them"
    },
    {
      icon: "UserCheck",
      text: "People should take ownership, not wait for instructions"
    }
  ],
  outcome: "When leaders are free from day-to-day operations, they rediscover why they started. They gain the space to think, to innovate, and to lead with purpose.",
  closing: "Growth isn't just about numbers. It's about building a business that feels alive, resilient, and in control."
};

export const solutionData = {
  title: "We Systemize Businesses — Not Just Fix Problems",
  subtitle: "Zenova studies how your business actually operates — not how it looks on paper.",
  tagline: "Starting at ₹1,20,000/year for companies up to 300 employees",
  analyzeAreas: [
    { icon: "Workflow", text: "Daily workflows" },
    { icon: "GitBranch", text: "Approval dependencies" },
    { icon: "Users", text: "People and role clarity" },
    { icon: "IndianRupee", text: "Cash flow movement" },
    { icon: "AlertTriangle", text: "Escalations and bottlenecks" }
  ],
  results: [
    "Operations don't stop when you step away",
    "Teams follow systems, not moods",
    "Cash flow becomes predictable",
    "Decisions are driven by data",
    "You finally get time to focus on growth"
  ]
};

export const strengthFramework = {
  title: "The STRENGTH Framework™",
  subtitle: "From Chaos to Control",
  description: "Zenova's proprietary 8-step systemization framework that transforms owner-dependent businesses into scalable organizations.",
  steps: [
    {
      letter: "S",
      name: "Study",
      icon: "Search",
      description: "We map every workflow to uncover hidden bottlenecks and inefficiencies."
    },
    {
      letter: "T",
      name: "Tweak",
      icon: "Settings",
      description: "Processes and SOPs are redesigned for scale, clarity, and accountability."
    },
    {
      letter: "R",
      name: "Re-Train",
      icon: "GraduationCap",
      description: "Teams are trained to follow systems — not depend on the owner."
    },
    {
      letter: "E",
      name: "Embed",
      icon: "Layers",
      description: "New workflows are implemented directly into daily operations."
    },
    {
      letter: "N",
      name: "Normalize",
      icon: "Monitor",
      description: "Manual work is digitized using dashboards, CRMs, and tools."
    },
    {
      letter: "G",
      name: "Gauge",
      icon: "Gauge",
      description: "KPIs and audits provide visibility without constant follow-ups."
    },
    {
      letter: "T",
      name: "Trim",
      icon: "Scissors",
      description: "Redundant work is eliminated. Automation replaces repetition."
    },
    {
      letter: "H",
      name: "Harden",
      icon: "Shield",
      description: "Backup systems ensure operations run even when people leave."
    }
  ],
  closing: "This isn't theory. It's execution — built for real businesses."
};

export const servicesData = {
  title: "Complete Business Systemization Suite",
  subtitle: "Every service your business needs for sustainable growth",
  services: [
    {
      id: 1,
      icon: "Search",
      title: "Business System Audit",
      description: "We begin by understanding how your business actually runs.",
      features: ["Workflow mapping", "Bottleneck identification", "Approval dependency analysis", "Role clarity gaps", "Cash flow visibility check", "We study SOPs run at 10X companies"],
      note: "This audit becomes the foundation of your transformation."
    },
    {
      id: 2,
      icon: "FileText",
      title: "Operations & SOP Architecture",
      description: "We convert chaos into clarity through written systems.",
      features: ["End-to-end SOP creation", "Department-wise protocols", "Escalation matrices", "Accountability structures", "We run systems and forms that are run by your industry leader"],
      note: "Your business stops relying on memory and verbal instructions."
    },
    {
      id: 3,
      icon: "Users",
      title: "People & Accountability Systems",
      description: "People don't fail. Systems do.",
      features: ["Role clarity & ownership mapping", "KPI-based performance tracking", "Structured reporting", "Reduced dependency on owner"],
      note: "Teams start operating independently — with confidence."
    },
    {
      id: 4,
      icon: "IndianRupee",
      title: "Cash Flow & Financial Control",
      description: "Visibility creates control.",
      features: ["Cash flow tracking & forecasting", "Receivables & payables systems", "Real-time financial dashboards", "Reduced cash crunch surprises"],
      note: "You stop reacting — and start planning."
    },
    {
      id: 5,
      icon: "BarChart3",
      title: "Dashboards, KPIs & Data Visibility",
      description: "No more blind decision-making.",
      features: ["Management dashboards", "Department-wise KPIs", "Automated reporting", "Audit-driven insights"],
      note: "Your desk stops being the help desk."
    },
    {
      id: 6,
      icon: "Rocket",
      title: "90-Day System Implementation",
      description: "This is where transformation happens.",
      features: ["On-ground implementation", "Team training & adoption", "Monthly audits", "Continuous optimization"],
      note: "We stay until systems work — without you."
    }
  ]
};

export const journeyData = {
  title: "A Structured 90-Day Transformation Journey",
  subtitle: "How Zenova Works",
  phases: [
    {
      name: "Phase 1",
      title: "Study & Mapping",
      icon: "Search",
      description: "Deep audit of departments, workflows, and dependencies.",
      duration: "Week 1-2"
    },
    {
      name: "Phase 2",
      title: "System Design",
      icon: "FileText",
      description: "SOPs, escalation matrices, role clarity, and formats.",
      duration: "Week 3-4"
    },
    {
      name: "Phase 3",
      title: "Digital Embedding",
      icon: "Monitor",
      description: "Dashboards, tools, automation, and reporting systems.",
      duration: "Week 5-8"
    },
    {
      name: "Phase 4",
      title: "Monitoring & Hardening",
      icon: "Shield",
      description: "Audits, KPI reviews, and resilience building.",
      duration: "Week 9-12"
    }
  ],
  closing: "We don't rush systems. We build them to last."
};

export const afterZenovaData = {
  title: "Life After Systemization",
  benefits: [
    { icon: "Building2", text: "Your business runs even when you're not present" },
    { icon: "Zap", text: "Decisions are faster and data-backed" },
    { icon: "UserCheck", text: "Teams take ownership, not permission" },
    { icon: "TrendingDown", text: "Escalations reduce dramatically" },
    { icon: "Eye", text: "Cash flow visibility improves" },
    { icon: "TrendingUp", text: "Growth becomes predictable" }
  ],
  conclusion: "You move from firefighting to foresight."
};

export const roiCalculatorData = {
  title: "Calculate Your Transformation ROI",
  subtitle: "See your potential savings with Zenova's business systemization",
  zenovaCost: 120000, // Annual cost ₹1.20 Lakh
  efficiencyGain: 0.85, // 85% reduction in chaos
  minRevenue: 10000000, // Minimum ₹1 Crore
  revenueIncrement: 0.05, // 5% increment
  avgSalary: 22000, // Average salary for calculation
  teamSizeMultiple: 25, // Team size calculation multiple
  industries: [
    { name: "Manufacturing", multiplier: 1.2 },
    { name: "Textiles", multiplier: 1.15 },
    { name: "IT Services", multiplier: 1.3 },
    { name: "Retail & Distribution", multiplier: 1.1 },
    { name: "Pharmaceuticals", multiplier: 1.25 },
    { name: "Food Processing", multiplier: 1.1 },
    { name: "Auto Components", multiplier: 1.2 },
    { name: "Engineering", multiplier: 1.15 },
    { name: "FMCG", multiplier: 1.1 },
    { name: "Chemicals", multiplier: 1.15 },
    { name: "Electronics", multiplier: 1.2 },
    { name: "Others", multiplier: 1.0 }
  ]
};

export const comparisonData = {
  title: "Before vs After Zenova",
  subtitle: "See the transformation in your daily operations",
  comparisons: [
    { aspect: "Decision Making", before: "Everything needs your approval", after: "Teams decide using clear protocols" },
    { aspect: "Daily Operations", before: "Constant firefighting", after: "Smooth, predictable workflows" },
    { aspect: "Employee Absence", before: "Operations crumble", after: "Backup systems kick in" },
    { aspect: "Information Flow", before: "WhatsApp chaos", after: "Documented SOPs & dashboards" },
    { aspect: "Cash Flow", before: "Surprises every month", after: "Predictable & visible" },
    { aspect: "Your Role", before: "Chief Problem Solver", after: "Strategic Leader" },
    { aspect: "Growth", before: "Feels exhausting", after: "Feels exciting" },
    { aspect: "Time Off", before: "Business stops", after: "Business continues" }
  ]
};

export const testimonialsData = {
  title: "Trusted by Growing Indian Businesses",
  subtitle: "See what founders say about their transformation",
  testimonials: [
    {
      id: 1,
      name: "Rajesh Kumar",
      role: "Managing Director",
      company: "Precision Auto Parts Pvt Ltd",
      location: "Pune",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
      quote: "Before Zenova, I couldn't take a single day off without my phone buzzing constantly. Now, I took a 2-week vacation and operations ran smoothly. My team follows systems, not my instructions.",
      stats: { metric: "Owner Dependency", before: "100%", after: "15%" }
    },
    {
      id: 2,
      name: "Priya Sharma",
      role: "CEO",
      company: "TechFab Industries",
      location: "Chennai",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face",
      quote: "We were spending 80% of our time solving the same problems repeatedly. Zenova helped us create SOPs and accountability systems. Now problems get solved at the right level without escalating to me.",
      stats: { metric: "Escalations to Owner", before: "50/week", after: "5/week" }
    },
    {
      id: 3,
      name: "Amit Patel",
      role: "Founder",
      company: "Gujarat Textiles",
      location: "Ahmedabad",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
      quote: "Cash flow was a constant headache. We never knew where the money was going. Zenova's financial control systems gave us complete visibility. No more month-end surprises.",
      stats: { metric: "Cash Flow Visibility", before: "30%", after: "95%" }
    },
    {
      id: 4,
      name: "Sneha Reddy",
      role: "Director",
      company: "Hyderabad Pharma Solutions",
      location: "Hyderabad",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
      quote: "When my operations manager resigned, I panicked. But thanks to Zenova's systems, his replacement was productive within a week. The knowledge was in the systems, not just in his head.",
      stats: { metric: "New Hire Ramp-up", before: "3 months", after: "1 week" }
    },
    {
      id: 5,
      name: "Vikram Singh",
      role: "Owner",
      company: "Delhi Machinery Works",
      location: "Delhi NCR",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face",
      quote: "I was working 14-hour days and still felt behind. Now I work 8 hours, and the business is growing faster than before. Zenova didn't just save my business, they saved my health and family time.",
      stats: { metric: "Work Hours", before: "14 hrs/day", after: "8 hrs/day" }
    }
  ]
};

export const statsData = {
  stats: [
    { value: 90, suffix: " Days", label: "To Complete Transformation", icon: "Building2" },
    { value: 85, suffix: "%", label: "Reduction in Owner Dependency", icon: "TrendingDown" },
    { value: 3, suffix: "x", label: "Revenue Growth Potential", icon: "Users" },
    { value: 3, suffix: "x", label: "Output with same resources", icon: "Star" }
  ]
};

export const industriesData = {
  title: "Built for Every Industry",
  industries: [
    "Manufacturing", "Textiles", "Pharmaceuticals", "Auto Components",
    "Food Processing", "Engineering", "Chemicals", "Electronics",
    "FMCG", "Distribution", "Retail", "IT Services"
  ]
};

export const targetAudienceData = {
  title: "This Is for You If…",
  criteria: [
    { icon: "Building2", text: "You're an SME owner or founder with 50-500 employees" },
    { icon: "User", text: "Your business depends too much on you" },
    { icon: "TrendingUp", text: "Growth feels chaotic instead of controlled" },
    { icon: "Shield", text: "You want freedom without losing control" }
  ]
};

export const idealCompanyData = {
  title: "Built for Ambitious Indian SMEs",
  subtitle: "Ready to Double Your Revenue?",
  description: "Zenova works with companies that have proven their business model and are ready to scale systematically.",
  badge: "₹5 Crore+ Revenue",
  criteria: [
    {
      icon: "IndianRupee",
      title: "₹5 Crore+ Annual Revenue",
      description: "You've built a successful business. Now it's time to build systems that match your ambition."
    },
    {
      icon: "TrendingUp",
      title: "3x Growth Ambition",
      description: "You're not looking for incremental gains. You want to double your revenue without doubling your stress."
    },
    {
      icon: "Users",
      title: "25-500 Employees",
      description: "Large enough to need systems, small enough to implement them quickly and see immediate impact."
    },
    {
      icon: "Target",
      title: "Ready to Delegate",
      description: "You understand that scaling requires letting go of day-to-day control — with the right systems in place."
    }
  ],
  stats: [
    { value: "2x", label: "Average Revenue Growth" },
    { value: "85%", label: "Reduction in Owner Time" },
    { value: "90 Days", label: "To See Results" }
  ],
  cta: "Is Your Company Ready?"
};

export const sapComparisonData = {
  title: "You Don't Need SAP",
  subtitle: "Stop Overpaying for Complexity You'll Never Use",
  description: "SAP was built for Fortune 500 companies with thousands of employees and unlimited budgets. Your ₹5-50 Crore business deserves a solution built for your reality.",
  highlight: "Save 85% vs SAP",
  comparisons: [
    // {
    //   aspect: "Annual Cost",
    //   zenova: "₹1.20 Lakhs",
    //   sap: "₹50+ Lakhs",
    //   others: "₹12 Lakhs",
    //   zenovaNote: "All-inclusive",
    //   sapNote: "Plus consultants",
    //   othersNote: "Basic features only"
    // },
    {
      aspect: "Implementation",
      zenova: "90 Days",
      sap: "12-24 Months",
      others: "6-8 Months",
      zenovaNote: "Results from Week 3",
      sapNote: "Delayed ROI",
      othersNote: "Standard rollout"
    },
    {
      aspect: "Complexity",
      zenova: "Built for SMEs",
      sap: "Built for MNCs",
      others: "Built for SMEs",
      zenovaNote: "Use 100% of features",
      sapNote: "Use only 10%",
      othersNote: "Moderate fit"
    },
    {
      aspect: "Training",
      zenova: "1-2 Days",
      sap: "2-4 Weeks",
      others: "2-4 Weeks",
      zenovaNote: "Intuitive interface",
      sapNote: "Steep learning curve",
      othersNote: "Moderate learning"
    },
    {
      aspect: "Customization",
      zenova: "Affordable & Fast",
      sap: "Expensive & Slow",
      others: "Mid-range",
      zenovaNote: "Included in price",
      sapNote: "₹10L+ per change",
      othersNote: "Entry-level expensive"
    },
    {
      aspect: "Support",
      zenova: "India-based 24/7",
      sap: "Global (Offshore)",
      others: "India-based",
      zenovaNote: "Same timezone",
      sapNote: "Ticket queues",
      othersNote: "Business hours"
    }
  ],
  bottomLine: {
    title: "The Bottom Line",
    zenova: {
      label: "Zenova",
      description: "Get systems that work for your business size, at a price that makes sense, with support that understands Indian SMEs."
    },
    sap: {
      label: "SAP",
      description: "Pay enterprise prices for enterprise complexity that your team won't fully utilize."
    }
  },
  cta: "Choose Smarter, Not Bigger"
};

export const ctaData = {
  title: "Build a Business That Doesn't Need You Every Day",
  subtitle: "Join Indian SMEs who chose systematic growth",
  description: "Zenova doesn't just improve businesses. We return control, clarity, and confidence to founders. Book a free 7-minute demo and see how we can transform your operations.",
  buttonText: "Book Your Free Demo",
  email: "info@zenovagrowthpartners.com",
  phone: "+91 98765 43210"
};

export const retrainSectionData = {
  title: "Re-Train Your Team for Systems",
  subtitle: "End Owner Dependency Forever",
  description: "The biggest bottleneck in any growing SME is owner dependency. Our Re-Train program teaches your employees to operate on systems, not on your availability.",
  badge: "Part of STRENGTH Framework",
  features: [
    {
      icon: "UserX",
      title: "Break Owner Dependency",
      description: "Train employees to make decisions using documented systems and protocols, not by waiting for your approval."
    },
    {
      icon: "BookOpen",
      title: "SOP-Based Operations",
      description: "Every team member learns to follow Standard Operating Procedures, ensuring consistency even when you're not present."
    },
    {
      icon: "RefreshCw",
      title: "Continuous Skill Building",
      description: "Regular training sessions keep your team updated on new processes and best practices."
    },
    {
      icon: "Users",
      title: "Cross-Functional Training",
      description: "Build backup capabilities so operations never stop when key people are unavailable."
    }
  ],
  outcomes: [
    "Employees make decisions independently",
    "Operations run without your daily involvement",
    "Teams follow systems, not verbal instructions",
    "New hires get productive in days, not months"
  ],
  cta: "See How Re-Training Works"
};

export const navLinks = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/#services" },
  { label: "Framework", href: "/#framework" },
  { label: "ROI Calculator", href: "/#calculator" },
  { label: "Testimonials", href: "/#testimonials" }
];

export const faqData = {
  title: "Frequently Asked Questions",
  faqs: [
    {
      question: "What exactly does Zenova do?",
      answer: "We systemize businesses. We study how your business actually operates, identify bottlenecks and dependencies, then rebuild it using written protocols, digital systems, dashboards, and accountability frameworks. The result is a business that runs smoothly even when you're not there."
    },
    {
      question: "How is this different from hiring a consultant?",
      answer: "Consultants give advice and leave. We implement and embed systems directly into your daily operations. We stay until the systems work without you. We don't create dependency on us — we eliminate dependency on you."
    },
    // {
    //   question: "What's included in the ₹1,20,000/year pricing?",
    //   answer: "This includes the complete 90-day transformation: Business audit, SOP creation, team training, dashboard setup, accountability systems, and 3 months of monitoring & optimization. For companies up to 300 employees."
    // },
    {
      question: "How long does implementation take?",
      answer: "Our structured 90-day transformation journey covers: Study & Mapping (Week 1-2), System Design (Week 3-4), Digital Embedding (Week 5-8), and Monitoring & Hardening (Week 9-12). You'll see results from week 3 onwards."
    },
    {
      question: "Will my team resist these changes?",
      answer: "Change resistance is common, which is why our Re-Train phase is critical. We work with your team, not against them. Systems are designed to make their jobs easier, not harder. Most teams embrace the clarity that comes with proper processes."
    },
    {
      question: "What happens after the 90 days?",
      answer: "By day 90, your systems are hardened and self-sustaining. We offer optional quarterly reviews and can help with new challenges as your business grows. But the goal is independence — your business running on systems, not on Zenova."
    }
  ]
};

export const whyZenovaData = {
  title: "Why Choose Zenova?",
  points: [
    { icon: "Target", title: "Execution-Focused", description: "We don't just advise — we implement" },
    { icon: "Heart", title: "Founder-Centric", description: "Built by founders, for founders" },
    { icon: "Building2", title: "SME Specialists", description: "Designed for 50-500 employee companies" },
    { icon: "Zap", title: "Quick Results", description: "See improvements from week 3" }
  ],
  closing: "We don't create dependency on consultants. We eliminate dependency on the owner."
};
